import { useState } from 'react';
import { IoHome, IoCreate, IoCheckmarkCircle, IoPeople, IoLogoGoogle, IoEye, IoEyeOff } from 'react-icons/io5';
import { signInWithGooglePopup, createUserWithEmailAndPassword, sendVerificationEmail, signInWithEmailAndPassword } from '../../../utils/firebase.utils';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { notifyBackendOfFirebaseAuth } from '../../../services/authService';
import { showAuthError } from '../../../utils/toast';
import ToastService from '../../../utils/toast';

const HomeIcon = IoHome as React.ComponentType<{ className?: string }>;
const CreateIcon = IoCreate as React.ComponentType<{ className?: string }>;
const CheckmarkCircleIcon = IoCheckmarkCircle as React.ComponentType<{ className?: string }>;
const PeopleIcon = IoPeople as React.ComponentType<{ className?: string }>;
const LogoGoogleIcon = IoLogoGoogle as React.ComponentType<{ className?: string }>;

const features = [
  {
    icon: <HomeIcon className="text-orange-500" />, bg: 'bg-orange-100', label: 'Build Your College List',
  },
  {
    icon: <CreateIcon className="text-blue-500" />, bg: 'bg-blue-100', label: 'Instant Essay Feedback',
  },
  {
    icon: <CheckmarkCircleIcon className="text-green-500" />, bg: 'bg-green-100', label: 'SMS Application Updates',
  },
  {
    icon: <PeopleIcon className="text-purple-500" />, bg: 'bg-purple-100', label: 'Access Our Live Counselors',
  },
];

const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<{ 
    email?: string; 
    password?: string; 
    confirmPassword?: string;
    displayName?: string;
  }>({});

  const validateForm = () => {
    const newErrors: { 
      email?: string; 
      password?: string; 
      confirmPassword?: string;
      displayName?: string;
    } = {};
    
    if (!displayName.trim()) {
      newErrors.displayName = 'Full name is required';
    } else if (displayName.trim().length < 2) {
      newErrors.displayName = 'Name must be at least 2 characters';
    }
    
    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!email.includes('@') || !email.includes('.')) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!password.trim()) {
      newErrors.password = 'Password is required';
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleEmailSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || isLoading) return;
    
    setIsLoading(true);
    try {
      // Create user with Firebase
      const res = await createUserWithEmailAndPassword(email, password, displayName);
      
      if (res.user) {
        // Note: Custom verification email will be sent automatically by backend
        // No need to send Firebase verification email
        ToastService.showSuccess('Account created! Check your email for verification and welcome messages. Don\'t forget to check your spam folder!');
        
        // Get Firebase ID token and notify backend
        const idToken = await res.user.getIdToken();
        
        // Use the displayName we set, fallback to Firebase displayName, then to email prefix
        const userName = displayName || res.user.displayName || res.user.email?.split('@')[0] || 'User';
        
        console.log('Sending to backend:', {
          name: userName,
          email: res.user.email,
          photoURL: res.user.photoURL,
          idTokenLength: idToken.length
        });
        
        const data = await notifyBackendOfFirebaseAuth(
          idToken,
          userName,
          res.user.email || '',
          res.user.photoURL || null // Send null instead of empty string
        );
        
        console.log('Backend registration successful:', {
          is_new_user: data.is_new_user,
          user_id: data.user?.id,
          user_email: data.user?.email
        });
        
        // Log user type for debugging
        if (data.is_new_user) {
          console.log('New user detected - welcome and verification emails sent by backend');
        } else {
          console.log('Existing user - no welcome email needed');
        }
        
        const userWithBackendData = {
          uid: res.user.uid,
          email: res.user.email || '',
          displayName: displayName,
          photoURL: res.user.photoURL || '',
          is_new_user: data.is_new_user,
          ...data.user
        };
        
        updateUser(userWithBackendData);
        navigate('/');
      }
    } catch (error: any) {
      console.error('Signup error:', error);
      
      if (error.code === 'auth/email-already-in-use') {
        // This could be an incomplete registration - try to sign in and complete backend registration
        try {
          console.log('Email already exists in Firebase, attempting to sign in and complete registration...');
          const signInResult = await signInWithEmailAndPassword(email, password);
          
          if (signInResult.user) {
            // Try to complete backend registration
            const idToken = await signInResult.user.getIdToken();
            const userName = displayName || signInResult.user.displayName || signInResult.user.email?.split('@')[0] || 'User';
            
            const data = await notifyBackendOfFirebaseAuth(
              idToken,
              userName,
              signInResult.user.email || '',
              signInResult.user.photoURL || null
            );
            
            const userWithBackendData = {
              uid: signInResult.user.uid,
              email: signInResult.user.email || '',
              displayName: userName,
              photoURL: signInResult.user.photoURL || '',
              is_new_user: data.is_new_user,
              ...data.user
            };
            
            updateUser(userWithBackendData);
            ToastService.showSuccess('Account setup completed successfully!');
            navigate('/');
            return;
          }
        } catch (signInError: any) {
          console.error('Failed to complete registration:', signInError);
          if (signInError.code === 'auth/wrong-password') {
            showAuthError('This email is already registered with a different password. Please try signing in instead.');
          } else {
            showAuthError('This email is already registered. If you signed up with Google, please use "Continue with Google" to sign in.');
          }
        }
      } else if (error.code === 'auth/weak-password') {
        showAuthError('Password is too weak. Please choose a stronger password.');
      } else if (error.code === 'auth/invalid-email') {
        showAuthError('Invalid email address.');
      } else if (error.response?.status === 422) {
        console.error('422 Validation Error Details:', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        });
        
        // Try to extract specific validation error message
        const validationMessage = error.response?.data?.detail || 
                                 error.response?.data?.message || 
                                 'Registration data validation failed';
        
        showAuthError(`Validation Error: ${validationMessage}`);
      } else if (error.message?.includes('Request failed')) {
        showAuthError('Registration failed due to server error. Please try again in a moment.');
      } else {
        showAuthError('Account creation failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignup = async () => {
    if (isGoogleLoading) return;
    
    setIsGoogleLoading(true);
    try {
      const res = await signInWithGooglePopup();
      
      if (res.user) {
        const idToken = await res.user.getIdToken();
        const data = await notifyBackendOfFirebaseAuth(
          idToken,
          res.user.displayName || '',
          res.user.email || '',
          res.user.photoURL || null
        );
        
        const userWithBackendData = {
          uid: res.user.uid,
          email: res.user.email || '',
          displayName: res.user.displayName || '',
          photoURL: res.user.photoURL || '',
          is_new_user: data.is_new_user,
          ...data.user
        };
        
        updateUser(userWithBackendData);
        navigate('/');
      }
    } catch (error) {
      showAuthError('Google signup failed. Please try again.');
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleLoginRedirect = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-2xl p-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-400 to-pink-500 flex items-center justify-center mx-auto mb-6">
            <span className="text-white text-3xl">
              <i className="fas fa-graduation-cap" />
            </span>
          </div>
          
          <h1 className="text-2xl font-bold text-center mb-2 font-inter">
            Join Apply.AI
          </h1>
          
          <p className="text-gray-600 text-center mb-6">
            Create your account to get started
          </p>
          
          <div className="space-y-4 mb-8">
            {features.map((f, i) => (
              <div key={i} className="flex items-center">
                <span className={`w-7 h-7 rounded-full ${f.bg} flex items-center justify-center mr-3`}>
                  {f.icon}
                </span>
                <span className="text-gray-700 text-base">{f.label}</span>
              </div>
            ))}
          </div>
          
          {/* Email/Password Signup Form */}
          <form onSubmit={handleEmailSignup} className="space-y-4 mb-6">
            <div>
              <input
                type="text"
                value={displayName}
                onChange={(e) => {
                  setDisplayName(e.target.value);
                  if (errors.displayName) setErrors({ ...errors, displayName: undefined });
                }}
                placeholder="Full name"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                  errors.displayName ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                }`}
                disabled={isLoading}
              />
              {errors.displayName && (
                <p className="text-red-500 text-sm mt-1">{errors.displayName}</p>
              )}
            </div>
            
            <div>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errors.email) setErrors({ ...errors, email: undefined });
                }}
                placeholder="Email address"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                  errors.email ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                }`}
                disabled={isLoading}
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
            </div>
            
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errors.password) setErrors({ ...errors, password: undefined });
                }}
                placeholder="Password"
                className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                  errors.password ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                }`}
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                disabled={isLoading}
              >
                {showPassword ? <IoEyeOff size={20} /> : <IoEye size={20} />}
              </button>
              {errors.password && (
                <p className="text-red-500 text-sm mt-1">{errors.password}</p>
              )}
            </div>
            
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);
                  if (errors.confirmPassword) setErrors({ ...errors, confirmPassword: undefined });
                }}
                placeholder="Confirm password"
                className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                  errors.confirmPassword ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300'
                }`}
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                disabled={isLoading}
              >
                {showConfirmPassword ? <IoEyeOff size={20} /> : <IoEye size={20} />}
              </button>
              {errors.confirmPassword && (
                <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>
              )}
            </div>
            
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full ${isLoading 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600'
              } text-white font-bold text-base rounded-lg h-12 flex items-center justify-center space-x-2 transition-all`}
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Creating account...</span>
                </>
              ) : (
                <span>Create Account</span>
              )}
            </button>
          </form>
          
          {/* Divider */}
          <div className="relative mb-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">or</span>
            </div>
          </div>
          
          {/* Google Signup Button */}
          <button
            onClick={handleGoogleSignup}
            disabled={isGoogleLoading}
            className={`w-full ${isGoogleLoading 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-white hover:bg-gray-50 border border-gray-300'
            } text-gray-700 font-medium text-base rounded-lg h-12 flex items-center justify-center space-x-2 transition-all mb-6`}
          >
            {isGoogleLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                <span>Creating account...</span>
              </>
            ) : (
              <>
                <LogoGoogleIcon className="text-xl text-red-500" />
                <span>Continue with Google</span>
              </>
            )}
          </button>
          
          {/* Login Link */}
          <div className="text-center">
            <span className="text-gray-600 text-sm">Already have an account? </span>
            <button
              onClick={handleLoginRedirect}
              className="text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors"
              disabled={isLoading || isGoogleLoading}
            >
              Sign In
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;